import { Space } from "task-eva-ui";
import { CommonCard, IconWrapper, CertifyTimeText } from "../atom";
import { AuthType } from "../constants/auth-type";
import PersonCertifyIcon from "@/assets/icons/profile/person-certify-normal";
import EnterpriseCertifyIcon from "@/assets/icons/profile/enterprise-certify-normal";
import StudentCertifyIcon from "@/assets/icons/profile/student-certify-normal";
import RightArrow from "@/assets/icons/actions/right-arrow";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import WarningIcon from "@/assets/icons/common/warning-circle";
import PendingIcon from "@/assets/icons/common/pending";
import { CertifyStatus } from "../constants/certify-status";
import { AuthStatus } from "project-config/constants";
import { useGlobalStore } from "@/store/GlobalStore";

export function ArrowCard({
  type = AuthType.Person,
  handler = () => {},
  size = "middle",
  certifyStatus = CertifyStatus.NoCertified,
  authStatus = AuthStatus.NoCertified,
  width = "",
  cardStyleName = "",
  disabled = false,
}: {
  type?: AuthType;
  handler?: () => void;
  size?: string;
  certifyStatus?: CertifyStatus;
  authStatus?: AuthStatus;
  width?: string;
  cardStyleName?: string;
  disabled?: boolean;
}) {
  const t = getTranslateNsT("profile");
  const { studentCertifyInfo } = useGlobalStore();
  const titleMap = new Map([
    [AuthType.Person, t("status-6")],
    [AuthType.Enterprise, t("enterprise_certify")],
    [AuthType.Student, t("student_certify")],
  ]);
  const iconMap = new Map([
    [AuthType.Person, <PersonCertifyIcon />],
    [AuthType.Enterprise, <EnterpriseCertifyIcon />],
    [AuthType.Student, <StudentCertifyIcon />],
  ]);
  const timeMap = new Map([[AuthType.Student, t("user_change_student_tip")]]);
  const gapMap = new Map([
    ["middle", 6],
    ["large", 6],
    ["small", 0],
    ["uncertified-large", 6],
  ]);
  const pendingMap = new Map([
    [AuthType.Person, certifyStatus === CertifyStatus.PersonPending],
    [
      AuthType.Student,
      studentCertifyInfo !== undefined &&
        studentCertifyInfo.studentVerifyPending,
    ],
    [
      AuthType.Enterprise,
      certifyStatus === CertifyStatus.EnterprisePending ||
        certifyStatus === CertifyStatus.PersonChangeEnterprise,
    ],
  ]);
  const tipMap = new Map([
    [
      AuthType.Person,
      [CertifyStatus.PersonPending, CertifyStatus.PersonFailed].includes(
        certifyStatus
      )
        ? certifyStatus === CertifyStatus.PersonPending
          ? t("certification_under_review")
          : t("certification_review_failed")
        : "",
    ],
    [
      AuthType.Enterprise,
      certifyStatus === CertifyStatus.EnterprisePending ||
      certifyStatus === CertifyStatus.PersonChangeEnterprise
        ? t("certification_under_review")
        : certifyStatus === CertifyStatus.EnterpriseFailed ||
          certifyStatus === CertifyStatus.PersonalToEnterpriseFailed
        ? t("certification_review_failed")
        : "",
    ],
    [
      AuthType.Student,
      studentCertifyInfo !== undefined &&
      studentCertifyInfo?.studentVerifyFailed
        ? t("certification_review_failed")
        : studentCertifyInfo !== undefined &&
          studentCertifyInfo?.studentVerifyPending
        ? t("certification_under_review")
        : "",
    ],
  ]);
  const pending = pendingMap.get(type);
  const tip = tipMap.get(type);
  const paddingLeft = [CertifyStatus.EnterpriseChange].includes(certifyStatus)
    ? "24px"
    : "";
  const unCertified = [
    CertifyStatus.NoCertified,
    CertifyStatus.PersonFailed,
    CertifyStatus.EnterpriseFailed,
    CertifyStatus.PersonPending,
    CertifyStatus.EnterprisePending,
  ].includes(certifyStatus);
  return (
    <CommonCard
      size={size}
      width={width}
      style={{
        paddingLeft,
      }}
      className={`arrow-card ${cardStyleName}`}
      onClick={handler}
      disabled={disabled}
    >
      {tip && (
        <div className={pending ? "pending tip" : "tip"}>
          {pending ? <PendingIcon /> : <WarningIcon />}
          {tip}
        </div>
      )}
      <Space gap={10} center height="100%">
        <IconWrapper className="type-icon">{iconMap.get(type)}</IconWrapper>
        <Space gap={gapMap.get(size)} column>
          <div className="title">
            {titleMap.get(type)}
            {unCertified && timeMap.get(type) && (
              <CertifyTimeText>{timeMap.get(type)}</CertifyTimeText>
            )}
          </div>
          {type === AuthType.Student ? (
            <div className="sub-title">
              {studentCertifyInfo !== undefined &&
              studentCertifyInfo.studentVerifyPending ? (
                <span style={{ color: "#0C9AD6" }}>
                  {t("certify_submit_wait_please")}
                </span>
              ) : studentCertifyInfo !== undefined &&
                studentCertifyInfo?.studentVerifyFailed ? (
                <span style={{ color: "#DF4036" }}>
                  {t("certify_common_fail_retry_please")}
                </span>
              ) : (
                <>
                  <span>{t("user_student_certification_sub_1")}</span>
                  <span className="interval">|</span>
                  <span>{t("user_student_certification_sub_2")}</span>
                </>
              )}
            </div>
          ) : type === AuthType.Person ? (
            <div className="sub-title">
              {certifyStatus === CertifyStatus.PersonPending ? (
                <span style={{ color: "#0C9AD6" }}>
                  {t("certify_submit_wait_please")}
                </span>
              ) : certifyStatus === CertifyStatus.PersonFailed ? (
                <span style={{ color: "#DF4036" }}>
                  {t("ceritfy_fail_retry_please")}
                </span>
              ) : (
                <>
                  <span>{t("user_personal_certification_sub_1")}</span>
                  <span className="interval">|</span>
                  <span>{t("user_personal_certification_sub_2")}</span>
                  <span className="interval">|</span>
                  <span>{t("user_personal_certification_sub_3")}</span>
                </>
              )}
            </div>
          ) : (
            <div className="sub-title">
              {certifyStatus === CertifyStatus.PersonalToEnterpriseFailed ? (
                <span style={{ color: "#DF4036" }}>
                  {t("ceritfy_enterprise_fail_retry_please")}
                </span>
              ) : certifyStatus === CertifyStatus.EnterprisePending ||
                certifyStatus === CertifyStatus.PersonChangeEnterprise ? (
                <span style={{ color: "#0C9AD6" }}>
                  {t("certify_submit_wait_please")}
                </span>
              ) : certifyStatus === CertifyStatus.EnterpriseFailed ? (
                <span style={{ color: "#DF4036" }}>
                  {t("ceritfy_enterprise_fail_retry_please")}
                </span>
              ) : (
                <>
                  <span>{t("user_enterprise_certification_sub_1")}</span>
                  <span className="interval">|</span>
                  <span>{t("user_enterprise_certification_sub_2")}</span>
                  <span className="interval">|</span>
                  <span>{t("user_enterprise_certification_sub_3")}</span>
                </>
              )}
            </div>
          )}
        </Space>
      </Space>
      <div className="right">
        <RightArrow fill="#757879" />
      </div>
    </CommonCard>
  );
}
